/**
 * `BuiltinAliasFactory` for `calendar-list` — closes over an
 * app-supplied `CalendarConfigProvider`. The factory's `dynamicTail`
 * reads from the provider directly; no `sheet.calendarConfig` field
 * required on the rows-side.
 *
 * `CalendarConfigProvider` is the contract a host fulfills (e.g. it
 * reads `client.calendars.peekAll()` and reshapes each entry into
 * `CalendarConfigCalendar`). The factory lives here
 * because calendar-list is part of the lona-js layer's value-add over
 * the rows-side primitives.
 */

import type { Option } from "@tento-lona/sheets";
import { Rational } from "@tento-core/rational";
import {
  type BuiltinAliasFactory,
  type CalendarConfigCalendar,
  type CalendarConfigProvider,
  CalendarId,
  type CellValue,
  type ChildSpec,
} from "@tento-lona/sheets";
import { calendarConfigCell } from "@tento-lona/sheets/platform";

/**
 * The built-in alias factory that expands a calendar-list alias into one row
 * per calendar the provider lists.
 */
export function calendarListAliasFactory(
  calendarConfig: CalendarConfigProvider,
): BuiltinAliasFactory {
  return {
    templateName: "alias.calendar-list",
    template: {
      displayName: "Calendars",
      // Data address (data-address migration): the calendar-list's events
      // live at the lona-native aggregate `:~lona:calendar:primary:events`,
      // but that address is still produced by the descriptor-level
      // `AliasResolver.batchKey` (`row-descriptor.ts` CalendarList).
      // Declaring a `dataRef` here is DEFERRED to Phase E: since Phase B,
      // `expandAliasInstance` consumes `dataRef` to re-key the alias row
      // itself, which for calendar-list would re-key every hydrated
      // virtual calendar-list child — a hot-calendar-path change that
      // must land together with the EventsTable attach re-pointing
      // (data-address/migration-plan.md Phase E), not silently ride
      // along with the weather migration.
      rule: {
        kind: "static",
        children: [
          {
            preset: "events",
            type: "list",
            label: "Events",
            position: Rational.nth(0),
            storage: "own",
            placement: "child",
            attributes: { type: "list" },
        children: [
          {
            // Default (plain column layout) presentation: the
            // column's events as a flat list. Without this the
            // alias's cell binding falls through to the data-json
            // dump when the sheet is not in a calendar layout.
            preset: "renderer-column",
            type: "column.event-list",
            position: Rational.nth(0),
            storage: "parent",
            placement: "child",
          },
          {
            preset: "renderer-timeline-row",
                type: "timeline[row].event",
                position: Rational.nth(1),
                storage: "parent",
                placement: "child",
              },
              {
                preset: "renderer-timeline-column",
                type: "timeline[column].event",
                position: Rational.nth(2),
                storage: "parent",
                placement: "child",
              },
              {
                preset: "renderer-grid",
                type: "grid.event",
                position: Rational.nth(3),
                storage: "parent",
                placement: "child",
              },
            ],
          },
        ],
      },
      dynamicTail: {
        parentPreset: "events",
        items: () => calendarConfig.calendars().map(calendarConfigCell),
        as: (item, _props, index): ChildSpec => {
          const id = calendarId(item, index);
          return {
            preset: `cal-${calendarPresetId(item, id)}`,
            type: "data-source",
            label: calendarLabel(item, index),
            position: Rational.nth(4 + index),
            storage: "own",
            placement: "child",
            // Top-level `lookupKey` so `Sheet.expand` lifts it into
            // `rowMetadata.lookupKey` — required for canonical cell
            // fetches (`:~google:{authId}:calendar:{calId}:events`).
            lookupKey: calendarEventsLookupKey(item, id),
            // Client-known invariant for these provider-backed rows:
            // cells arrive in `event.v1` wire format. Without this
            // `Sheet.encodingForRowId` returns undefined and
            // `SourceRow.ensureLoaded` skips decoding — the canonical
            // `Event` shape never materializes and `EVENT_SHAPE.probe`
            // rejects the raw payload.
            meta: { encoding: "event.v1" },
            attributes: {
              dtype: "obj",
              cellCardinality: "multi",
            },
          };
        },
        filter: (item, local) => {
          const id = calendarId(item, 0);
          return local.visibleByKey.get(id) ?? true;
        },
      },
    },
  };
}

// ── Per-calendar helpers (used by the factory's dynamicTail) ──

/**
 * A `calendar-list` config cell with no server-rendered `eventsLookupKey`.
 * The server owns events-row key construction (provider prefix, primary
 * normalization); a client-built fallback silently mis-keyed every
 * non-google calendar, so a cell without the key is refused.
 */
export class MissingEventsLookupKeyError extends Error {
  override name = "MissingEventsLookupKeyError";
  constructor(readonly calendarId: CalendarId) {
    super(
      `calendar-list: calendar config item for "${calendarId}" has no ` +
        `eventsLookupKey — pass CalendarDisplay.eventsLookupKey through the ` +
        `CalendarConfigProvider.`,
    );
  }
}

function calendarEventsLookupKey(item: CellValue, id: string): string {
  const eventsKey = asRecord(item)?.eventsLookupKey;
  if (typeof eventsKey === "string" && eventsKey.length > 0) {
    return eventsKey;
  }
  throw new MissingEventsLookupKeyError(CalendarId.of(id));
}

function calendarId(item: CellValue, index: number): string {
  const rec = asRecord(item);
  const id = rec?.id;
  if (typeof id === "string" && id.length > 0) return id;
  return `calendar-${index}`;
}

function calendarPresetId(item: CellValue, id: string): string {
  const rec = asRecord(item);
  const authId = rec?.authId;
  const prefix = typeof authId === "string" && authId.length > 0
    ? `${authId}-`
    : "";
  return `${prefix}${id}`.replaceAll(/[^A-Za-z0-9_-]/g, "_");
}

function calendarLabel(item: CellValue, index: number): string {
  const rec = asRecord(item);
  const summary = rec?.summary;
  if (typeof summary === "string" && summary.length > 0) return summary;
  const label = rec?.label;
  if (typeof label === "string" && label.length > 0) return label;
  return `Calendar ${index + 1}`;
}

function asRecord(
  value: CellValue,
): Option<Readonly<Record<string, CellValue>>> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Readonly<Record<string, CellValue>>
    : null;
}

// Re-export the provider interface so app code can satisfy it without
// reaching into the rows-side namespace.
export type { CalendarConfigCalendar, CalendarConfigProvider };
